"use client";

import React, {
  useState,
  useRef,
  useEffect,
  ReactNode,
  KeyboardEvent,
  useCallback,
} from "react";
import { Checkbox, Spinner } from "@lmn/design-system";
import IconArrowDown from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconArrowDown.js";
import IconTrash from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconTrash.js";
import {
  ArrowWrapper,
  CheckboxWrapper,
  ClearAllButton,
  Container,
  ContentWrapper,
  IconWrapper,
  Label,
  LoadingWrapper,
  OptionItem,
  OptionsList,
  Placeholder,
  SelectBox,
  TextWrapper,
  TruncatedText,
  SearchValueInput,
} from "./MultiSelect.styles";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  label: string;
  icon?: ReactNode;
  dataTestId?: string;
  loading?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  label,
  icon,
  dataTestId,
  loading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<(HTMLLIElement | null)[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [sortedOption, setSortedOption] = useState<MultiSelectOption[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const setOptionRef = useCallback(
    (el: HTMLLIElement | null, index: number) => {
      optionsRef.current[index] = el;
    },
    []
  );

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setFocusedIndex(-1);
      setSearchInput("");
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  useEffect(() => {
    setSortedOption(options);
  }, [options]);

  useEffect(() => {
    const sort = options.filter((item) =>
      item.label.toLowerCase().includes(searchInput.toLowerCase())
    );
    setSortedOption(sort);
  }, [searchInput, options]);

  const toggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const clearAll = () => {
    onChange([]);
    setIsOpen(false);
  };

  const displayValue =
    value.length > 0
      ? options
        .filter((option) => value.includes(option.value))
        .map((option) => option.label)
        .join(", ")
      : "";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    if (focusedIndex === -1) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    } else if (focusedIndex >= 0) {
      optionsRef.current[focusedIndex]?.focus();
    }
  }, [isOpen, focusedIndex]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        } else {
          setFocusedIndex((prev) => (prev + 1) % options.length);
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (isOpen) {
          setFocusedIndex(
            (prev) => (prev - 1 + options.length) % options.length
          );
        }
        break;
      case "Enter":
        event.preventDefault();
        if (isOpen && focusedIndex >= 0) {
          toggleOption(options[focusedIndex].value);
        } else {
          toggleDropdown();
        }
        break;
      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        break;
      default:
        if (isOpen) {
          setFocusedIndex(-1);
        }
        break;
    }
  };

  return (
    <Container ref={containerRef}>
      <SelectBox
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-labelledby="multiselect-label"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="options-list"
        tabIndex={0}
      >
        <ContentWrapper>
          {icon && <IconWrapper>{icon}</IconWrapper>}
          <TextWrapper>
            <Label id="multiselect-label">{label}</Label>
            {isOpen ? (
              <SearchValueInput
                type="text"
                value={searchInput}
                onChange={handleChange}
                placeholder={displayValue || placeholder}
                autoFocus={isOpen}
                ref={inputRef}
                data-testid={`${dataTestId}Button`}
              />
            ) : (
              <TruncatedText>
                {displayValue || <Placeholder>{placeholder}</Placeholder>}
              </TruncatedText>
            )}
          </TextWrapper>
        </ContentWrapper>
        <ArrowWrapper $isOpen={isOpen} aria-hidden="true">
          <IconArrowDown size={16} />
        </ArrowWrapper>
      </SelectBox>
      <OptionsList
        $isOpen={isOpen}
        role="listbox"
        id="options-list"
        aria-labelledby="multiselect-label"
        aria-multiselectable="true"
        aria-busy={loading}
        data-testid={dataTestId}
      >
        {loading ? (
          <LoadingWrapper data-testid={`${dataTestId}Loading`}>
            <Spinner size={24} />
          </LoadingWrapper>
        ) : (
          <>
            {value.length > 0 && (
              <ClearAllButton
                onClick={(e) => {
                  e.stopPropagation();
                  clearAll();
                }}
              >
                <IconTrash size={20} />
                Auswahl entfernen
              </ClearAllButton>
            )}
            {sortedOption.map((option, index) => (
              <OptionItem
                key={option.value}
                onClick={() => toggleOption(option.value)}
                role="option"
                aria-selected={value.includes(option.value)}
                tabIndex={isOpen ? 0 : -1}
                ref={(el) => setOptionRef(el, index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleOption(option.value);
                  }
                }}
              >
                <CheckboxWrapper>
                  <Checkbox
                    checkbox={{
                      id: option.value,
                      label: option.label,
                    }}
                    isChecked={value.includes(option.value)}
                    onChange={() => {}} // We handle changes in the OptionItem onClick
                  />
                </CheckboxWrapper>
              </OptionItem>
            ))}
          </>
        )}
      </OptionsList>
    </Container>
  );
};

export default MultiSelect;
